import { TGlobalStores } from '.';
import { IAssemblyData } from '../data/assembly/assembly';
import { Curve2dUtils } from '../math/Curve2dUtils';
import { IPoint2d } from '../math/IPoint2d';
import { getRotatedRectangleVertices } from '../math/utils';
import { booleanPolygon, BooleanPolygonType } from '../model/polygon';
import { BaseListStore } from './base/baseListStore';

export class AssemblyStore {
  private _assemblyDataMap: BaseListStore<IAssemblyData>;

  constructor(gs: TGlobalStores) {
    this._assemblyDataMap = new BaseListStore<IAssemblyData>(gs);
  }

  getAssemblyDatas() {
    return this._assemblyDataMap.getList();
  }

  updateAssemblyData(assemblyDataId: string, updateParams: Partial<IAssemblyData>) {
    this._assemblyDataMap.frequentlyUpdate(assemblyDataId, updateParams);
  }

  isAssemblyFuniture(funitureId: string) {
    const assemblyDatas = this.getAssemblyDatas();
    for (const assemblyData of assemblyDatas) {
      const findFunitureId = assemblyData.accessorieIds.find((accessorieId) => accessorieId === funitureId);
      if (findFunitureId) {
        return true;
      }
    }

    return false;
  }

  public getAssemblyIdByFunitureId(funitureId: string) {
    const assemblyDatas = this.getAssemblyDatas();
    for (const assemblyData of assemblyDatas) {
      const findFunitureId = assemblyData.accessorieIds.find((accessorieId) => accessorieId === funitureId);
      if (findFunitureId) {
        return assemblyData.id;
      }
    }
  }

  getRelateModelAssemblyId(funitureId: string) {
    const assemblyDatas = this.getAssemblyDatas();
    for (const assemblyData of assemblyDatas) {
      const findFunitureId = assemblyData.accessorieIds.find((accessorieId) => accessorieId === funitureId);
      if (findFunitureId) {
        return assemblyData.id;
      }
    }

    return undefined;
  }

  getAssemblyData(assemblyId: string) {
    return this._assemblyDataMap.get(assemblyId);
  }

  addAssemblyData(assemblyData: IAssemblyData) {
    this._assemblyDataMap.add(assemblyData);
  }

  removeAssemblyDataById(assemblyId: string) {
    this._assemblyDataMap.remove(assemblyId);
  }

  getAssemblyInPolygon(
    polygon: IPoint2d[],
    holes: IPoint2d[][] = [],
    mode: 'intersect' | 'center' | 'contain' = 'center'
  ) {
    switch (mode) {
      case 'contain':
        return this.getAssemblyDatas().filter((assembly) => {
          const box = getRotatedRectangleVertices(
            assembly.width,
            assembly.thickness,
            assembly.center,
            assembly.rotation
          );

          // 先排除在孔洞内的组件
          for (const hole of holes) {
            if (box.some((point) => Curve2dUtils.isPointInPolygon(point, hole))) {
              return false;
            }
          }
          if (box.every((point) => Curve2dUtils.isPointInPolygon(point, polygon))) {
            return true;
          }
          return false;
        });
      case 'intersect':
        return this.getAssemblyDatas().filter((assembly) => {
          const box = getRotatedRectangleVertices(
            assembly.width,
            assembly.thickness,
            assembly.center,
            assembly.rotation
          );

          // 先排除在孔洞内的组件
          for (const hole of holes) {
            if (box.every((point) => Curve2dUtils.isPointInPolygon(point, hole))) {
              return false;
            }
          }
          if (box.some((point) => Curve2dUtils.isPointInPolygon(point, polygon))) {
            return true;
          }

          // 顶点判断覆盖不到「两者相交但互不含对方顶点」——如窄带横穿包围盒、
          // 或选框整个落在包围盒内部，用 booleanPolygon 兜底
          if (booleanPolygon({ main: [polygon], assistant: [box], type: BooleanPolygonType.Intersect }).length > 0) {
            return true;
          }

          return false;
        });
      case 'center':
        return this.getAssemblyDatas().filter((assembly) => {
          const { center } = assembly;

          // 先排除在孔洞内的组件
          for (const hole of holes) {
            if (Curve2dUtils.isPointInPolygon(center, hole)) {
              return false;
            }
          }
          if (!Curve2dUtils.isPointInPolygon(center, polygon)) {
            return false;
          }
          return true;
        });
    }
  }

  clearAll() {
    this._assemblyDataMap.clear();
  }

  coverDatas(assemblyDatas: IAssemblyData[]) {
    this.clearAll();

    assemblyDatas.forEach((assemblyData) => {
      this.addAssemblyData(assemblyData);
    });
  }
}
